import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import StatusBadge from '../../components/ui/StatusBadge';
import CapacityBar from '../../components/ui/CapacityBar';
import TabsComponent from '../../components/ui/TabsComponent';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../firebase/config';
import DailyRoutinesWidget from '../../components/dashboard/DailyRoutinesWidget';
import SimpleTaskForm from '../../components/SimpleTaskForm';
import { Trash2 } from 'lucide-react';
import { 
  CheckSquare, 
  AlertTriangle,
  Clock,
  Activity,
  Calendar,
  X,
  Plus,
  FolderOpen,
  FileText,
  Globe,
  User,
  Edit,
  RotateCcw,
  Sun,
  Moon,
  ClipboardList,
  Target,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SimpleProjectForm from '../../components/SimpleProjectForm';
import { renderTasksContent, renderProjectsContent } from '../../components/dashboard/InlineContent';

const EmployeeDashboard = ({ activeTab, setActiveTab }) => {
  const { currentUser } = useAuth();
  const { tasks = [], projects = [], users = [], departments = [], teams = [],
          updateProject, updateTask, updateUser, createProject, createTask, deleteProject, deleteTask } = useData();
  const navigate = useNavigate();
  
  // State for inline content display
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedTab, setSelectedTab] = useState('personal');

  const handleBoxClick = (contentType, tabType) => {
    setSelectedContent(contentType);
    setSelectedTab(tabType);
  };

  // Render inline content based on selection
  const renderInlineContent = () => {
    if (!selectedContent) return null;

    const isPersonal = selectedTab === 'personal';
    
    switch (selectedContent) {
      case 'tasks':
        const filteredTasks = isPersonal 
          ? tasks.filter(t => 
              t.scope === 'personal' && 
              (t.assigneeId === currentUser?.id || t.createdBy === currentUser?.id) &&
              !shouldHideTask(t)
            )
          : tasks.filter(t => 
              (t.assigneeId === currentUser?.id || 
               (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(currentUser?.id))) &&
              t.scope !== 'personal' &&
              !shouldHideTask(t)
            );
        return renderTasksContent(filteredTasks, users, currentUser, isPersonal, setSelectedContent, navigate);
        
      case 'projects':
        const filteredProjects = isPersonal
          ? projects.filter(p => 
              p.scope === 'personal' && 
              (p.assigneeId === currentUser?.id || p.createdBy === currentUser?.id)
            )
          : projects.filter(p => 
              (p.assigneeId === currentUser?.id || 
               (Array.isArray(p.assigneeIds) && p.assigneeIds.includes(currentUser?.id))) &&
              p.scope !== 'personal'
            );
        return renderProjectsContent(filteredProjects, tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
        
      default:
        return null;
    }
  };

  // State for modals
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);

  // Helper functions
  const shouldHideTask = (task) => {
    // הסתר משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
    if (task.isDailyRoutine || task.routineId || task.createdFrom === 'daily_routine') {
      return true;
    }
    
    // בדיקה נוספת - משימות שהשם שלהן מכיל תאריך (סימן למשימת שגרה)
    if (task.title && task.title.includes(' - ') && task.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      return true;
    }
    
    return false;
  };

  const getProjectStatus = (project) => {
    if (!project) return 'לא ידוע';
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    if (projectTasks.length === 0) return 'ממתין לעבודה';
    const completedTasks = projectTasks.filter(t => t.status === 'הושלם').length;
    const totalTasks = projectTasks.length;
    if (completedTasks === 0) return 'ממתין לעבודה';
    if (completedTasks === totalTasks) return 'פרויקט הושלם';
    return 'פרויקט בתהליך';
  };

  // Stats card component
  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600',
      purple: 'bg-purple-50 text-purple-600'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  // Get my tasks and projects
  const myPersonalTasks = tasks.filter(t => 
    t.scope === 'personal' && 
    (t.assigneeId === currentUser?.id || t.createdBy === currentUser?.id) &&
    !shouldHideTask(t)
  );

  const myAssignedTasks = tasks.filter(t => 
    (t.assigneeId === currentUser?.id || 
     (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(currentUser?.id))) &&
    t.scope !== 'personal' &&
    !shouldHideTask(t)
  );

  const myPersonalProjects = projects.filter(p => 
    p.scope === 'personal' && 
    (p.assigneeId === currentUser?.id || p.createdBy === currentUser?.id)
  );

  const myAssignedProjects = projects.filter(p => 
    (p.assigneeId === currentUser?.id || 
     (Array.isArray(p.assigneeIds) && p.assigneeIds.includes(currentUser?.id))) &&
    p.scope !== 'personal'
  );

  // Tabs configuration
  const tabs = (() => {
    const personalTab = {
      id: 'personal',
      label: 'אישי',
      icon: User,
      badge: myPersonalTasks.length.toString(),
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">העבודה האישית שלי</h2>
            <p className="text-sm text-gray-600 mt-1">נהל את המשימות והפרויקטים האישיים שלך</p>
          </div>
        <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Personal Projects Card */}
              <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים אישיים</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {myPersonalProjects.length}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים פעילים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const activeProjects = myPersonalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length === 0 || done < pts.length;
                      });
                      const completedProjects = myPersonalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length > 0 && done === pts.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-green-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Personal Tasks Card */}
              <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות אישיות</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">
                    {myPersonalTasks.length}
                  </p>
                  <p className="text-sm text-gray-600">משימות פעילות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const activeTasks = myPersonalTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = myPersonalTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
                title="משימות פעילות" 
                value={myPersonalTasks.filter(t => t.status !== 'הושלם').length} 
          icon={CheckSquare} 
                color="green"
        />
        <StatCard 
                title="משימות דחופות" 
                value={myPersonalTasks.filter(t => 
                  t.priority === 'important_urgent' || t.priority === 'not_important_urgent'
                ).length} 
                icon={AlertTriangle} 
                color="red"
        />
        <StatCard 
                title="פרויקטים פעילים" 
                value={myPersonalProjects.filter(p => getProjectStatus(p) !== 'פרויקט הושלם').length} 
                icon={FolderOpen} 
                color="blue"
        />
        <StatCard 
                title="השלמות השבוע" 
                value={myPersonalTasks.filter(t => {
                  if (t.status !== 'הושלם') return false;
                  const completedDate = new Date(t.updatedAt);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return completedDate >= weekAgo;
                }).length} 
                icon={Target} 
                color="purple"
        />
      </div>

            {/* תוכן מפורט שנפתח כשלוחצים על כרטיסייה */}
            {renderInlineContent()}

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => setShowNewProject(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
                  <FolderOpen size={16} />
              פרויקט חדש
            </button>
            <button 
              onClick={() => setShowNewTask(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
                  <CheckSquare size={16} />
              משימה חדשה
            </button>
          </div>
        </div>
                          </div>
        </div>
      )
    };

    const workTab = {
      id: 'work',
      label: 'עבודה',
      icon: ClipboardList,
      badge: myAssignedTasks.length.toString(),
      content: (
        <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">המשימות שהוטלו עלי</h2>
            <p className="text-sm text-gray-600 mt-1">נהל את המשימות והפרויקטים שהוקצו לך</p>
            </div>
            <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Assigned Projects Card */}
              <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'work')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-blue-600" />
                            </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים מוקצים</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {myAssignedProjects.length}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים פעילים</p>
                          </div>
                        </div>
                        
              {/* Assigned Tasks Card */}
              <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'work')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-orange-600" />
                          </div>
                  <h3 className="text-lg font-semibold mb-2">משימות מוקצות</h3>
                  <p className="text-3xl font-bold text-orange-600 mb-2">
                    {myAssignedTasks.length}
                  </p>
                  <p className="text-sm text-gray-600">משימות פעילות</p>
                        </div>
            </div>
          </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard 
                title="משימות פעילות" 
                value={myAssignedTasks.filter(t => t.status !== 'הושלם').length} 
                icon={Activity} 
                color="blue"
              />
              <StatCard 
                title="משימות דחופות" 
                value={myAssignedTasks.filter(t => 
                  t.priority === 'important_urgent' || t.priority === 'not_important_urgent'
                ).length} 
                icon={AlertTriangle} 
                color="red"
              />
              <StatCard 
                title="לא אושרו" 
                value={myAssignedTasks.filter(t => !t.acknowledged).length} 
                icon={Clock} 
                color="yellow"
              />
              <StatCard 
                title="בסיכון/חסום" 
                value={myAssignedTasks.filter(t => 
                  t.status === 'בסיכון' || t.status === 'חסום'
                ).length} 
                icon={AlertTriangle} 
                color="red"
                          />
                        </div>

            {/* תוכן מפורט שנפתח כשלוחצים על כרטיסייה */}
            {renderInlineContent()}
          </div>
        </div>
      )
    };

    return [personalTab, workTab];
  })();

  return (
    <div className="p-6">
  <TabsComponent tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

     {/* Daily Routines Section */}
<DailyRoutinesWidget />

      {/* New Project Modal */}
      {showNewProject && (
        <SimpleProjectForm
          onClose={() => setShowNewProject(false)}
          onSave={async (projectData) => {
            await createProject(projectData);
                      setShowNewProject(false);
          }}
        />
      )}

      {/* New Task Modal */}
      {showNewTask && (
        <SimpleTaskForm
          isOpen={showNewTask}
          onClose={() => setShowNewTask(false)}
          onSubmit={async (taskData) => {
            await createTask(taskData);
                      setShowNewTask(false);
          }}
          currentUser={currentUser}
          users={users}
          projects={projects}
        />
      )}
    </div>
  );
};

export default EmployeeDashboard;
